import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import apiService from '../services/api';
import usePageTitle from '../hooks/usePageTitle';
import { logger } from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { usePrelaunch } from '../contexts/PrelaunchContext';
import PublicPageLayout from '../components/layout/PublicPageLayout';
import CanonicalLink from '../components/common/CanonicalLink';
import FAQSection from '../components/landing/FAQSection';

interface Price {
  id: string;
  interval: string;
  amount: number;
  currency: string;
}

interface Plan {
  id: string;
  name: string;
  description: string;
  features: string[];
  prices: Price[];
  seatLimit?: number;
  monthlySimQuota?: number;
  accountLimit?: number;
  visiblePublic?: boolean;
}

const PricingPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isPrelaunch } = usePrelaunch();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState('year');
  
  // Set the page title
  usePageTitle('WaveScoop | Pricing', false);
  
  // Fetch pricing plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoading(true);
        const data = await apiService.getSubscriptionPlans();
        if (data && data.plans) {
          setPlans(data.plans);
          setError(null);
        } else {
          setError('Unable to load pricing plans');
        }
      } catch (err) {
        logger.error('Error fetching plans:', err);
        setError('Failed to fetch pricing plans. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPlans();
  }, []);
  
  // Handle billing cycle toggle
  const handleBillingCycleChange = (
    event: React.MouseEvent<HTMLElement>,
    newBillingCycle: string,
  ) => {
    if (newBillingCycle !== null) {
      setBillingCycle(newBillingCycle);
    }
  };
  
  // Format currency
  const formatCurrency = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0
    }).format(amount);
  };
  
  // Get price for the selected billing cycle
  const getPrice = (plan: Plan) => {
    const price = plan.prices.find(p => p.interval === billingCycle);
    return price ? formatCurrency(price.amount, price.currency) : 'Contact us';
  };
  
  // Get price ID for selected plan and billing cycle
  const getPriceId = (plan: Plan) => {
    const price = plan.prices.find(p => p.interval === billingCycle);
    return price ? price.id : '';
  };
  
  // Get button text based on plan
  const getButtonText = (plan: Plan) => {
    if (plan.id === 'trial') return 'Start Free Trial';
    return isAuthenticated ? 'Choose Plan' : 'Sign Up';
  };
  
  // Helper to get button link based on plan
  const getButtonLink = (plan: Plan) => {
    if (plan.id === 'trial') return '/login?source=pricing';
    
    const priceId = getPriceId(plan);
    
    // Append plan name, billing cycle, and price ID as query params
    return `/login?plan=${plan.id}&billing=${billingCycle}&priceId=${priceId}&source=pricing`;
  };

  // Helper to determine if a plan should be highlighted
  const isHighlightedPlan = (plan: Plan) => {
    return plan.id === 'starter'; // Highlight the Starter plan
  };

  // Use plans provided by backend (includes trial) and filter out any missing IDs
  const displayPlans = plans.filter(plan => plan && plan.id);
  // Public visibility filter
  const visiblePlans = displayPlans.filter((p) => p.visiblePublic !== false);
  
  return (
    <PublicPageLayout>
      <Helmet>
        <meta 
          name="description" 
          content="Choose the perfect WaveScoop plan for your team. Start with a free trial or upgrade to Pro for advanced social media sentiment analysis and post simulation features." 
        />
      </Helmet>
      <CanonicalLink />
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Box sx={{ px: 3, pt: 8, pb: 10 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
              <CircularProgress sx={{ color: 'white' }} />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h5" color="error" gutterBottom>
                {error}
              </Typography>
              <Button variant="contained" onClick={() => window.location.reload()}>
                Retry
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                <Box component="section" sx={{ textAlign: 'center', mb: 6 }}>
                  <Typography variant="h3" component="h1" gutterBottom sx={{ color: 'white' }}>
                    Choose the Right Plan
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 4, maxWidth: 800, mx: 'auto', color: 'white', opacity: 0.8 }}>
                    All plans include our core sentiment prediction features, real-time analytics, and dashboard access.
                  </Typography>
                  
                  <ToggleButtonGroup
                    value={billingCycle}
                    exclusive
                    onChange={handleBillingCycleChange}
                    aria-label="billing cycle"
                    sx={{
                      mb: 4,
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      '& .MuiToggleButton-root': {
                        color: 'white',
                        textTransform: 'none',
                        px: 3,
                      },
                      '& .MuiToggleButton-root.Mui-selected': {
                        color: 'white',
                        bgcolor: 'rgba(25, 118, 210, 0.5)',
                      }
                    }}
                  >
                    <ToggleButton value="month">
                      Monthly
                    </ToggleButton>
                    <ToggleButton value="year">
                      Yearly (Save 17%)
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
              
              <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                <Grid container spacing={4} justifyContent="center">
                  {visiblePlans.map((plan) => (
                    <Grid item key={plan.id} xs={12} md={displayPlans.length === 3 ? 4 : 6} lg={4}>
                      <Paper
                      component="article"
                      elevation={isHighlightedPlan(plan) ? 8 : 3}
                      sx={{
                        p: 4,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        bgcolor: isHighlightedPlan(plan) 
                          ? 'rgba(10, 42, 77, 0.8)'
                          : 'rgba(15, 51, 89, 0.65)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: 4,
                        border: isHighlightedPlan(plan) 
                          ? '1px solid rgba(25, 118, 210, 0.3)' 
                          : '1px solid rgba(255, 255, 255, 0.1)',
                        transition: 'transform 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                        }
                      }}
                    >
                      {isHighlightedPlan(plan) && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 40,
                            transform: 'translateY(-50%)',
                            bgcolor: '#1976d2',
                            color: 'white',
                            py: 0.5,
                            px: 2,
                            borderRadius: 1,
                            fontWeight: 'bold',
                            fontSize: '0.875rem',
                          }}
                        >
                          Most Popular
                        </Box>
                      )}
                      
                      <Typography variant="h4" component="h2" gutterBottom sx={{ color: 'white' }}>
                        {plan.name}
                      </Typography>
                      
                      <Typography variant="body2" sx={{ mb: 2, color: 'white', opacity: 0.8 }}>
                        {plan.description}
                      </Typography>
                      
                      {plan.id === 'trial' ? (
                        <Box sx={{ textAlign: 'center', color: 'white', minHeight: 160, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <Typography variant="h3" component="p" sx={{ mt: 2, mb: 1, fontWeight: 'bold', color: 'white' }}>
                            Free
                          </Typography>
                        </Box>
                      ) : (
                        (() => {
                          const monthly = plan.prices.find(p => p.interval === 'month');
                          const yearly = plan.prices.find(p => p.interval === 'year');
                          if (billingCycle === 'year' && yearly) {
                            const perMonthRounded = Math.ceil(yearly.amount / 12);
                            const fullYearly = monthly ? monthly.amount * 12 : null;
                            const savings = fullYearly ? Math.round((1 - (yearly.amount / fullYearly)) * 100) : null;
                            return (
                              <Box sx={{ textAlign: 'center', color: 'white', minHeight: 160, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                                  <Typography variant="h3" component="p" sx={{ mt: 2, mb: 0.5, fontWeight: 'bold', color: 'white' }}>
                                    {formatCurrency(perMonthRounded, yearly.currency)}
                                  </Typography>
                                  {monthly && (
                                    <Typography component="span" variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'line-through' }}>
                                      {formatCurrency(monthly.amount, monthly.currency)}
                                    </Typography>
                                  )}
                                </Box>
                                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)' }}>per month</Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                  Billed annually at {formatCurrency(yearly.amount, yearly.currency)} / year
                                </Typography>
                                {savings !== null && (
                                  <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 'bold' }}>Save {savings}%</Typography>
                                )}
                              </Box>
                            );
                          }
                          // Monthly view (or yearly missing): show standard monthly pricing
                          const price = plan.prices.find(p => p.interval === 'month') || plan.prices[0];
                          return (
                            <Box sx={{ textAlign: 'center', color: 'white', minHeight: 160, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                              <Typography variant="h3" component="p" sx={{ mt: 2, mb: 1, fontWeight: 'bold', color: 'white' }}>
                                {formatCurrency(price.amount, price.currency)}
                                <Typography component="span" variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 1 }}>
                                  /month
                                </Typography>
                              </Typography>
                            </Box>
                          );
                        })()
                      )}
                      
                      <Box sx={{ flexGrow: 1, my: 2 }}>
                        {plan.features.map((feature) => (
                          <Box key={feature} sx={{ py: 1, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                            <Typography sx={{ color: 'white' }}>{feature}</Typography>
                          </Box>
                        ))}
                      </Box>
                      
                      {!isPrelaunch && (plan.id === 'trial' ? !isAuthenticated : (plan.id !== 'starter' && plan.id !== 'pro')) && (
                        <Button 
                          variant={isHighlightedPlan(plan) ? "contained" : "outlined"}
                          color="primary"
                          size="large"
                          component={Link}
                          to={getButtonLink(plan)}
                          sx={{ 
                            mt: 'auto',
                            py: 1.5,
                            borderRadius: 2,
                            ...(plan.id !== 'pro' && { color: 'white', borderColor: 'white' })
                          }}
                        >
                          {getButtonText(plan)}
                        </Button>
                      )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
              
              <Box sx={{ mt: 10, textAlign: 'center' }}>
                <Typography variant="h4" component="h2" gutterBottom sx={{ color: 'white' }}>
                  Enterprise Solution
                </Typography>
                <Typography sx={{ mb: 4, maxWidth: 800, mx: 'auto', color: 'white', opacity: 0.8 }}>
                  Need a custom solution for your organization? Contact our sales team for a tailored plan.
                </Typography>
                {!isPrelaunch && (
                  <Button 
                    variant="contained" 
                    color="primary"
                    size="large"
                    component="a"
                    href="mailto:support@wavescoop.com"
                    sx={{ 
                      py: 1.5,
                      px: 4,
                      borderRadius: 2 
                    }}
                  >
                    Contact Us
                  </Button>
                )}
              </Box>

              {/* FAQ Section */}
              <FAQSection
                title="Pricing & Billing Questions"
                subtitle="Clear answers about plans, payments, and your account"
                questions={[
                  {
                    question: "Can I change my plan anytime?",
                    answer: "Yes, you can upgrade or downgrade your plan at any time from your account settings. When you upgrade, you'll be charged a prorated amount for the remainder of your billing cycle. When you downgrade, the change will take effect at the start of your next billing cycle, and you'll retain access to your current plan features until then."
                  },
                  {
                    question: "What payment methods do you accept?",
                    answer: "We accept all major credit cards (Visa, Mastercard, American Express, Discover) and debit cards through our secure payment processor, Stripe. All payments are processed securely and we never store your payment information on our servers."
                  },
                  {
                    question: "Is there a free trial available?",
                    answer: "Yes! We offer a 14-day free trial that gives you access to our core features including idea generation and simulation capabilities. No credit card is required to start your trial. You'll have 5 ideas / simulations to test during the trial period, allowing you to fully experience how WaveScoop can transform your content strategy."
                  },
                  {
                    question: "What happens when I reach my idea / simulation limit?",
                    answer: "When you approach your monthly idea / simulation limit, we'll send you a notification. If you reach the limit, you can either wait for your quota to reset at the start of your next billing cycle or upgrade to a higher plan for additional ideas / simulations."
                  },
                  {
                    question: "Do you offer refunds?",
                    answer: "We offer a 30-day money-back guarantee for new customers on their first payment. If you're not satisfied with WaveScoop within your first 30 days as a paying customer, contact our support team for a full refund. For monthly subscriptions beyond the first month, you can cancel anytime and you won't be charged for the next billing cycle."
                  },
                  {
                    question: "Can I add team members to my account?",
                    answer: "Team collaboration features are available on our Pro and Enterprise plans. The Pro plan includes up to 5 team members, while Enterprise plans can be customized for larger teams. Each team member gets their own login and can access shared workspaces, allowing for seamless collaboration on content creation and review."
                  }
                ]}
              />
            </>
          )}
        </Box>
      </Box>
    </PublicPageLayout>
  );
};

export default PricingPage; 